import React, { useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import {
  CustomModal,
  CustomButton,
  CustomTextInput,
  CustomCalendarInput,
  CustomLoading,
} from "@ippon/shared-components";
import { formatDateForInput } from "@ippon/shared-utils";
import { Can } from "@/components/auth/Can";

interface DepartureProcessModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantName: string | null;
  contractStartDate: string;
  contractEndDate: string;
  onSubmit?: (data: DepartureProcessFormData) => void | Promise<void>;
}

export interface DepartureProcessFormData {
  departureDate: string;
  departureReason?: string;
  note?: string;
}

const DepartureProcessModal: React.FC<DepartureProcessModalProps> = ({
  isOpen,
  onClose,
  tenantName,
  contractStartDate,
  contractEndDate,
  onSubmit,
}) => {
  const getCurrentDate = () => {
    return formatDateForInput(new Date());
  };

  const departureForm = useForm<DepartureProcessFormData>({
    defaultValues: {
      departureDate: getCurrentDate(),
      departureReason: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      departureForm.reset({
        departureDate: getCurrentDate(),
        departureReason: "",
      });
    }
  }, [isOpen, departureForm]);

  const handleClose = () => {
    departureForm.reset();
    onClose();
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = departureForm.handleSubmit(async (data) => {
    if (onSubmit) {
      setSubmitting(true);
      try {
        await onSubmit(data);
        handleClose();
      } catch {
        // エラー時はモーダルを閉じない
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
    <CustomModal
      openModal={isOpen}
      handleCloseModal={handleClose}
      title="退去処理"
      modalType="create"
      addTitleBorder
      confirmOnDirtyClose={departureForm.formState.isDirty}
    >
      <Box
        component="form"
        // ポータル描画でも React 合成イベントは React ツリーを伝播するため、
        // submit が外側の <form> (PropertyOverview) に届いて別トーストが
        // 二重発火する。モーダル境界で伝播を止める。
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(e);
        }}
        sx={{
          p: { xs: "18px 18px 0px 18px", sm: "24px 24px 0px 24px" },
          display: "flex",
          flexDirection: "column",
          position: "relative",
          // CustomModal の .modalBoxWrapper が padding:16px を持つので、
          // このモーダルでは pb のみ打ち消す (= 下余白 0)。
          mb: "-16px",
        }}
      >
        {submitting && (
          <Box sx={{ position: "absolute", inset: 0, zIndex: 10, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(255,255,255,0.7)", borderRadius: "8px" }}>
            <CustomLoading loading={true} fullScreen={false} size="medium" />
          </Box>
        )}
        <Stack
          spacing={1.5}
          sx={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          {/* 契約者名（グレーアウト） */}
          <Box>
            <Typography fontSize={12} fontWeight={700} mb={0.5}>
              契約者名
            </Typography>
            <Box
              sx={{
                p: 1,
                background: "#f3f4f6",
                borderRadius: "8px",
                border: "1px solid #D9D9D9",
              }}
            >
              <Typography fontSize={12} sx={{ minHeight: "1.2em" }}>
                {tenantName || "-"}
              </Typography>
            </Box>
          </Box>

          {/* 契約開始日（グレーアウト） */}
          <Box>
            <Typography fontSize={12} fontWeight={700} mb={0.5}>
              契約開始日
            </Typography>
            <Box
              sx={{
                p: 1,
                background: "#f3f4f6",
                borderRadius: "8px",
                border: "1px solid #D9D9D9",
              }}
            >
              <Typography fontSize={12} sx={{ minHeight: "1.2em" }}>{contractStartDate || "-"}</Typography>
            </Box>
          </Box>

          {/* 契約終了日（グレーアウト） */}
          <Box>
            <Typography fontSize={12} fontWeight={700} mb={0.5}>
              契約終了日
            </Typography>
            <Box
              sx={{
                p: 1,
                background: "#f3f4f6",
                borderRadius: "8px",
                border: "1px solid #D9D9D9",
              }}
            >
              <Typography fontSize={12} sx={{ minHeight: "1.2em" }}>{contractEndDate || "-"}</Typography>
            </Box>
          </Box>

          {/* 退去日 */}
          <CustomCalendarInput<DepartureProcessFormData>
            name="departureDate"
            control={departureForm.control as any}
            label="退去日"
            isRequired={true}
            rules={{ required: "退去日は必須です" }}
          />

          {/* 退去理由 */}
          <CustomTextInput
            label="退去理由"
            name="departureReason"
            placeholder="例: 契約満了、契約者都合による解約"
            register={departureForm.register}
            errors={departureForm.formState.errors}
            isRequired={false}
            inputWidth="100%"
          />

          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{
              flexShrink: 0,
              py: "8px",
              borderTop: "1px solid #e5e7eb",
              bgcolor: "white",
            }}
          >
            <Can action="contract:edit">
              <CustomButton
                label="退去処理"
                buttonCategory="submit"
                type="submit"
                disabled={submitting}
              />
            </Can>
          </Stack>
        </Stack>
      </Box>
    </CustomModal>
  );
};

export default DepartureProcessModal;
